import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { TenantNavigationService } from '../../services/tenant-navigation.service';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { ToastService } from '../../../shared/services/toast.service';
import { ActivatedRoute } from '@angular/router';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
  BottomNavUpdateState,
} from '../../../shared/bottom-nav/bottom-nav.service';
import { Observable, Subscription, tap } from 'rxjs';
import {
  MicrosoftCallingProductThreshold,
  MicrosoftSuggestedProduct,
  PrivilegedTenant,
  SuggestedProduct,
} from '../../../shared/models/generated/ziroModels';
import { MicrosoftCallingProductThresholdsResource } from './calling-product-thresholds.resource';
import {
  ZiroFormConfig,
  ZiroFormsValidationConfigItem,
  ZiroFormsValidationState,
} from '../../../forms/ziro-forms-models';
import { HtmlInputType, ZiroTextConfig } from '../../../forms/fields/text/ziro-text.component';
import { ZiroFormAbstractDirective } from '../../../forms/ziro-form-abstract.directive';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { ButtonStyles, ButtonTypes } from '../../../button/button.component';
import { isEqual } from 'lodash';

interface MicrosoftCallingProductThresholdFormJson extends MicrosoftCallingProductThreshold {
  incomingCalls: number;
  outgoingMinutes: number;
}

@Component({
  selector: 'ziro-microsoft-calling-product-thresholds',
  templateUrl: './calling-product-thresholds.component.html',
  styleUrls: ['../../admin-page.scss'],
  providers: [TenantNavigationService, MicrosoftCallingProductThresholdsResource],
  standalone: false,
})
export class MicrosoftCallingProductThresholdsComponent
  extends ZiroFormAbstractDirective<MicrosoftCallingProductThresholdFormJson, MicrosoftCallingProductThresholdFormJson>
  implements OnInit, OnDestroy
{
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _toastService = inject(ToastService);
  private _activatedRoute = inject(ActivatedRoute);
  private _bottomNavService = inject(BottomNavService);
  private _tenantNavigationService = inject(TenantNavigationService);
  private _microsoftCallingProductThresholdsResource = inject(MicrosoftCallingProductThresholdsResource);

  isLoading = true;
  suggestedPlanType = '';
  suggestedPlanDescription = '';
  formConfig: ZiroFormConfig;

  private _tenant: PrivilegedTenant;
  private _subscriptions = new Subscription();
  private _validator: ZiroFormsValidationConfigItem = {
    validator: (val: string) => {
      if (!Number.isInteger(parseFloat(val)) || val?.toString().includes('.')) {
        return ZiroFormsValidationState.INVALID;
      }

      return ZiroFormsValidationState.VALID;
    },
    message: 'tkey;admin.microsoft.calling_product_thresholds.form.integer_valid.message',
  };

  ngOnInit(): void {
    this._initFormConfig();
    this._initTenants();

    const subscription = this.ziroFormsUpdate$.subscribe((changes) => {
      if (!isEqual(changes.new, changes.old)) {
        this._getSuggestedPlan();
      }
    });
    this._subscriptions.add(subscription);

    const formSubmittedSub = this._validateAndSubmitSource.subscribe(() => {
      this.isErrorPresent.subscribe((hasError) => {
        this._setBottomNavValidationError(hasError);
      });
    });
    this._subscriptions.add(formSubmittedSub);

    const formUpdateSub = this.ziroFormsUpdateSource.subscribe(() => {
      if (this.isFormSubmitted) {
        this._setBottomNavValidationError(!this.isFormValid());
      }
    });
    this._subscriptions.add(formUpdateSub);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  protected submit(): Observable<void> {
    this._setBottomNavPending(true);

    return this._microsoftCallingProductThresholdsResource
      .put({
        incomingCallsThreshold: this.entity.incomingCallsThreshold,
        outgoingMinutesThreshold: this.entity.outgoingMinutesThreshold,
      })
      .pipe(
        tap(() => {
          this._setBottomNavPending(false);
          this._toastService.push(
            ToastTypes.SUCCESS,
            ZiroIcons.SAVE_SUCCESS,
            'tkey;shared.toast.save.success.title',
            'tkey;admin.microsoft.calling_product_thresholds.toast',
          );
        }),
      );
  }

  private _getSuggestedPlan(): void {
    if (
      !this.isFormValid() ||
      !this.entity ||
      !this.entity.incomingCallsThreshold ||
      !this.entity.outgoingMinutesThreshold ||
      !this.entity.incomingCalls ||
      !this.entity.outgoingMinutes
    ) {
      return;
    }

    const requestBody = {
      thresholds: {
        incomingCallsThreshold: this.entity.incomingCallsThreshold,
        outgoingMinutesThreshold: this.entity.outgoingMinutesThreshold,
      },
      exampleData: {
        exampleIncomingCalls: this.entity.incomingCalls,
        exampleOutgoingMinutes: this.entity.outgoingMinutes,
      },
    };

    this._microsoftCallingProductThresholdsResource
      .getSuggestedProducts(requestBody)
      .subscribe((data: MicrosoftSuggestedProduct) => {
        switch (data.suggestedProduct) {
          case SuggestedProduct.BUNDLED_MINUTES: {
            this.suggestedPlanType =
              'tkey;admin.microsoft.calling_product_thresholds.usage_example.calculated_plan.bundled_minute.label';
            this.suggestedPlanDescription =
              'tkey;admin.microsoft.calling_product_thresholds.usage_example.calculated_plan.bundled_minute.description';
            break;
          }
          case SuggestedProduct.SHARED_CALLING: {
            this.suggestedPlanType =
              'tkey;admin.microsoft.calling_product_thresholds.usage_example.calculated_plan.shared_calling.label';
            this.suggestedPlanDescription =
              'tkey;admin.microsoft.calling_product_thresholds.usage_example.calculated_plan.shared_calling.description';
            break;
          }
          case SuggestedProduct.PER_MINUTE: {
            this.suggestedPlanType =
              'tkey;admin.microsoft.calling_product_thresholds.usage_example.calculated_plan.per_minute.label';
            this.suggestedPlanDescription =
              'tkey;admin.microsoft.calling_product_thresholds.usage_example.calculated_plan.per_minute.description';
            break;
          }
          case SuggestedProduct.NOT_APPLICABLE:
          default: {
            this.suggestedPlanType = '';
            this.suggestedPlanDescription = '';
            break;
          }
        }
      });
  }

  private _initFormConfig(): void {
    this.formConfig = {
      fields: {
        incomingCallsThreshold: {
          label: 'tkey;admin.microsoft.calling_product_thresholds.incoming_calls_limit.label',
          helpText: 'tkey;admin.microsoft.calling_product_thresholds.incoming_calls_limit.helptext',
          dataAutomation: 'incoming-calls-threshold',
          componentConfig: new ZiroTextConfig({
            htmlInputType: HtmlInputType.NUMBER,
            min: 0,
          }),
          required: true,
          validation: [this._validator],
        },
        outgoingMinutesThreshold: {
          label: 'tkey;admin.microsoft.calling_product_thresholds.outgoing_minutes_threshold.label',
          helpText: 'tkey;admin.microsoft.calling_product_thresholds.outgoing_minutes_threshold.helptext',
          dataAutomation: 'outgoing-minutes-threshold',
          componentConfig: new ZiroTextConfig({
            htmlInputType: HtmlInputType.NUMBER,
            min: 0,
          }),
          required: true,
          validation: [this._validator],
        },
        incomingCalls: {
          label: '',
          dataAutomation: 'usage-example-incoming-calls',
          componentConfig: new ZiroTextConfig({
            htmlInputType: HtmlInputType.NUMBER,
            displayInline: true,
            inputWidth: '80px',
            min: 0,
          }),
          required: true,
          validation: [this._validator],
        },
        outgoingMinutes: {
          label: '',
          dataAutomation: 'usage-example-outgoing-minutes',
          componentConfig: new ZiroTextConfig({
            htmlInputType: HtmlInputType.NUMBER,
            displayInline: true,
            inputWidth: '80px',
            min: 0,
          }),
          required: true,
          validation: [this._validator],
        },
      },
    };
  }

  private _initTenants(): void {
    const tenantSub = this._tenantNavigationService.getTenantInfo(this._activatedRoute).subscribe((tenantInfo) => {
      this._tenant = tenantInfo;
      this._microsoftCallingProductThresholdsResource.setUrl(this._tenant.tenantId);

      this._initBreadcrumb();
      this._initBottomNav();
      this._getCallingProductThresholds();
    });
    this._subscriptions.add(tenantSub);
  }

  private _getCallingProductThresholds(): void {
    this._microsoftCallingProductThresholdsResource.get().subscribe((data: MicrosoftCallingProductThreshold) => {
      this.entitySource.next({
        outgoingMinutesThreshold: data.outgoingMinutesThreshold,
        incomingCallsThreshold: data.incomingCallsThreshold,
        incomingCalls: 5,
        outgoingMinutes: 50,
      });

      setTimeout(() => {
        this._getSuggestedPlan();
      });

      this.isLoading = false;
    });
  }

  private _initBreadcrumb(): void {
    this._breadcrumbsService.updateBreadcrumbs([
      {
        label: 'tkey;admin.tenants.title',
        routerLink: false,
      },
      {
        label: this._tenant.tenantName,
        url: `/admin/tenants/${this._tenant.tenantId}/app-registration`,
        routerLink: true,
      },
      { label: 'tkey;admin.microsoft.calling_product_thresholds.title' },
    ]);
  }

  private _initBottomNav(): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'microsoft-calling-products-threshold-save-button',
          dataAutomation: 'microsoft-calling-products-threshold-save-button',
          label: 'tkey;admin.ui.save',
          buttonClass: ButtonStyles.PRIMARY,
          state: {
            pending: false,
            buttonDisableState: {
              disabled: false,
              tooltipKey: '',
            },
          },
          icon: ZiroIcons.SAVE_SUCCESS,
          type: ButtonTypes.SUBMIT,
          submitSubject: this._validateAndSubmitSource,
        },
      ]),
    );
  }

  private _setBottomNavPending(setting: boolean): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'microsoft-calling-products-threshold-save-button',
        state: {
          pending: setting,
        },
      }),
    );
  }

  private _setBottomNavValidationError(hasError: boolean): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: hasError,
      }),
    );
  }
}
